import { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/Base/Dialog";
import { Button } from "@/components/Base/Button";
import { BuilderDialog } from "@/components/Automations/BuilderDialog";
import {
  AUTOMATION_ACTION_OPTIONS,
  AUTOMATION_TRIGGER_OPTIONS,
} from "@/lib/const";
import {
  BuilderAction,
  BuilderTrigger,
  TriggerCondition,
  CreateAutomationDto,
} from "@/types/v2/Automation";
import { Trigger } from "@/components/Automations/Trigger";
import { Action } from "@/components/Automations/Action";
import { CreateTaskActionValue } from "@/components/Automations/CreateTaskActionValue";
import { ScheduleValue } from "@/components/Automations/ScheduleValue";
import { ShowToastActionValue } from "@/components/Automations/ShowToastActionValue";
import { PlusIcon } from "lucide-react";
import { useDynamicOptions } from "@/hooks/useDynamicOptions";
import { useFeature } from "@/hooks/useFeatures";
import { FEATURE_FLAGS } from "@/types/v2/FeatureFlag";
import { parseCreateTaskActionValue } from "@/lib/automationCreateTask";
import { parseShowToastActionValue } from "@/lib/automationShowToast";
import {
  isTriggerSetSafe,
  LOOP_UNSAFE_ACTION_FIELDS,
} from "@/lib/automationTriggerSafety";
import { ScheduleScopeAlert } from "@/components/Automations/ScheduleScopeAlert";
import {
  hasOrphanScheduleConditions,
  isActionAllowedOnSchedule,
  isScheduleValueComplete,
  isUnconditionedSchedule,
  parseScheduleTriggerValue,
} from "@/lib/automationSchedule";
import {
  getVisibleTriggerOptions,
  getSelectableConditionalTriggers,
  hasContradictoryFixedValueConditions,
} from "@/lib/automationTriggerOptions";

interface AutomationDialogProps {
  onSubmit?: (automation: CreateAutomationDto) => Promise<void>;
}

export const AutomationDialog = ({ onSubmit }: AutomationDialogProps) => {
  const [open, setOpen] = useState(false);
  const [showTriggerBuilderDialog, setShowTriggerBuilderDialog] =
    useState(true);
  const [showActionBuilderDialog, setShowActionBuilderDialog] = useState(true);

  const [trigger, setTrigger] = useState<BuilderTrigger[]>();
  const [action, setAction] = useState<BuilderAction[]>();
  const [validationError, setValidationError] = useState<string | null>(null);
  const { generateOptions } = useDynamicOptions();
  const isScheduledAutomationsEnabled = useFeature(
    FEATURE_FLAGS.isScheduledAutomationsEnabled
  );

  // "On a schedule" is fired by a scheduled scanner in functions-v2. Hosting
  // deploys automatically on merge but functions deploy manually, so without
  // this gate a shop could build an automation before its backend exists and
  // it would silently never fire — and saved automations are snapshots, so
  // every one built in that window would activate at once when it lands.
  const availableTriggerOptions = useMemo(
    () =>
      getVisibleTriggerOptions({
        isScheduledAutomationsEnabled,
      }),
    [isScheduledAutomationsEnabled]
  );

  const handleTriggerClicked = (optionId: string, secondaryId?: string) => {
    const primaryTrigger = AUTOMATION_TRIGGER_OPTIONS.find(
      (option) => option.id === optionId
    );
    if (!primaryTrigger) return;
    const secondaryTrigger = primaryTrigger.conditionalTriggers?.find(
      (option) => option.id === secondaryId
    );
    // Clone before it enters state. AUTOMATION_TRIGGER_OPTIONS is a
    // module-level singleton, and the value handlers below used to assign
    // straight through the reference — so authoring "every Mon at 17:00" made
    // the trigger-008 CATALOG ENTRY that schedule for the rest of the browser
    // session, and the next automation opened pre-filled with it.
    if (secondaryTrigger) {
      const selected = { ...secondaryTrigger };
      setTrigger((prevState) =>
        prevState ? [...prevState, selected] : [selected]
      );
    } else {
      const selected = { ...primaryTrigger };
      setTrigger((prevState) =>
        prevState ? [...prevState, selected] : [selected]
      );
    }
  };

  const handleRemoveTrigger = (index: number) => {
    if (index === 0) {
      setTrigger(undefined);
      setShowTriggerBuilderDialog(true);
    } else {
      setTrigger((prevState) => prevState?.filter((_, i) => i !== index));
    }
  };

  const handleTriggerValueChange = (index: number, value: string) => {
    if (trigger) {
      // Build a NEW object rather than assigning through the existing one —
      // see handleTriggerClicked. Cloning on selection and mutating here would
      // still be safe, but only by accident; doing both makes it structural.
      //
      // Update in place by index — do NOT filter+append. Moving the edited
      // trigger to the end of the array silently reorders it relative to any
      // trigger added after it (e.g. a schedule's condition), which is what
      // WEBV3-192 was: editing the schedule's time after adding a condition
      // pushed the schedule past the condition in both the saved array and
      // the read-only display, which just renders stored order.
      //
      // The reachable branch below builds the updated trigger from prevState
      // inside the updater, not from the render closure's `trigger[index]` —
      // two updates batched into one React event would otherwise read the
      // same stale value and the second would clobber the first instead of
      // composing on top of it. The fallback branch (no prior state) has no
      // such race — nothing to be stale relative to — so it keeps spreading
      // from the closure, which is guaranteed defined inside this `if`.
      setTrigger((prevState) =>
        prevState
          ? prevState.map((t, i) =>
              i === index ? { ...t, triggerValue: value } : t
            )
          : [{ ...trigger[index], triggerValue: value }]
      );
    }
  };

  const handleTriggerConditionalChange = (
    index: number,
    value: TriggerCondition
  ) => {
    if (trigger) {
      // Same in-place-by-index update as handleTriggerValueChange, and for
      // the same reason: filter+append silently reorders. No catalog entry
      // in AUTOMATION_TRIGGER_OPTIONS currently sets triggerConditionOptions
      // (the field this feeds — see AutomationDialog.tsx's onConditionalChange
      // wiring), so this path is unreachable from the live builder today.
      // Fixed for consistency so the bug isn't reintroduced the day a trigger
      // does use it.
      //
      // The reachable branch below builds the updated trigger from prevState
      // inside the updater — same closure-read fix as handleTriggerValueChange.
      // The fallback branch (no prior state) has no race concern and keeps
      // spreading from the closure, which is guaranteed defined inside this `if`.
      setTrigger((prevState) =>
        prevState
          ? prevState.map((t, i) =>
              i === index ? { ...t, triggerCondition: value } : t
            )
          : [{ ...trigger[index], triggerCondition: value }]
      );
    }
  };

  const triggerButtons = {
    sections: [
      {
        title: "Shop Hero",
        buttons: availableTriggerOptions.map((option) => ({
          label: option.buttonOptions.text,
          icon: option.buttonOptions.icon,
          onClick: () => handleTriggerClicked(option.id),
          tooltip: option.buttonOptions.tooltip,
        })),
      },
    ],
  };

  const conditionButtons = useMemo(
    () =>
      trigger?.map((t) => {
        if (!t.conditionalTriggers) return undefined;
        const selectable = getSelectableConditionalTriggers(
          t.conditionalTriggers,
          trigger
        );
        if (selectable.length === 0) return undefined;
        return {
          sections: [
            {
              title: "Shop Hero",
              buttons: selectable.map((option) => ({
                label: option.buttonOptions.text,
                icon: option.buttonOptions.icon,
                onClick: () => handleTriggerClicked(t.id, option.id),
                tooltip: option.buttonOptions.tooltip,
              })),
            },
          ],
        };
      }),
    [trigger]
  );

  const additionalActions = useMemo(
    () =>
      action?.map((t) =>
        t.subsequentActions &&
        t.subsequentActions.filter(
          (ct) => !action?.map((tId) => tId.id).includes(ct.id)
        ).length > 0
          ? {
              sections: [
                {
                  title: "Shop Hero",
                  buttons:
                    t?.subsequentActions
                      .filter(
                        (ct) => !action?.map((tId) => tId.id).includes(ct.id)
                      )
                      .map((option) => ({
                        label: option.buttonOptions.text,
                        icon: option.buttonOptions.icon,
                        onClick: () => handleActionClicked(t.id, option.id),
                        tooltip: option.buttonOptions.tooltip,
                      })) ?? [],
                },
              ],
            }
          : undefined
      ),
    [action]
  );

  const handleActionClicked = (optionId: string, secondaryId?: string) => {
    const primaryAction = AUTOMATION_ACTION_OPTIONS.find(
      (option) => option.id === optionId
    );
    if (!primaryAction) return;
    const secondaryAction = primaryAction.subsequentActions?.find(
      (option) => option.id === secondaryId
    );
    if (secondaryAction) {
      const selected = { ...secondaryAction };
      const requiredActions = (secondaryAction.subsequentActions ?? [])
        .filter((a) => a.required)
        .map((a) => ({ ...a }));
      setAction((prevState) =>
        prevState
          ? [...prevState, selected, ...requiredActions]
          : [selected, ...requiredActions]
      );
    } else {
      const selected = { ...primaryAction };
      const requiredActions = (primaryAction.subsequentActions ?? [])
        .filter((a) => a.required)
        .map((a) => ({ ...a }));
      setAction((prevState) =>
        prevState
          ? [...prevState, selected, ...requiredActions]
          : [selected, ...requiredActions]
      );
    }
  };

  const handleRemoveAction = (index: number) => {
    if (index === 0) {
      setAction(undefined);
      setShowActionBuilderDialog(true);
    } else {
      setAction((prevState) => prevState?.filter((_, i) => i !== index));
    }
  };

  const handleActionValueChange = (index: number, value: string) => {
    if (action) {
      setAction((prevState) =>
        prevState?.map((a, i) =>
          i === index ? { ...a, actionValue: value } : a
        )
      );
    }
  };

  // A scheduled automation does not offer every action: see
  // SCHEDULE_EXCLUDED_ACTION_FIELDS. Filtering the picker is the affordance,
  // not the enforcement — a saved automation is a snapshot and import bypasses
  // this dialog entirely, so processScheduledAutomation drops excluded actions
  // again on the apply path.
  const isScheduled = (trigger ?? []).some(
    (t) => t.triggerEventType === "onSchedule"
  );
  const availableActionOptions = isScheduled
    ? AUTOMATION_ACTION_OPTIONS.filter((option) =>
        isActionAllowedOnSchedule(option.actionField)
      )
    : AUTOMATION_ACTION_OPTIONS;

  const actionButtons = {
    sections: [
      {
        title: "Shop Hero",
        buttons: availableActionOptions.map((option) => ({
          label: option.buttonOptions.text,
          icon: option.buttonOptions.icon,
          onClick: () => handleActionClicked(option.id),
          tooltip: option.buttonOptions.tooltip,
        })),
      },
    ],
  };

  const handleTriggerSelected = () => {
    setShowTriggerBuilderDialog(false);
  };

  const handleActionSelected = () => {
    setShowActionBuilderDialog(false);
  };

  const resetDialog = () => {
    setTrigger(undefined);
    setAction(undefined);
    setShowTriggerBuilderDialog(true);
    setShowActionBuilderDialog(true);
    setValidationError(null);
  };

  const handleSave = () => {
    if (!trigger || !action) {
      return;
    }
    setValidationError(null);
    const hasCreateTask = action.some((a) => a.actionField === "createTask");
    const loopUnsafe = action.filter((a) =>
      LOOP_UNSAFE_ACTION_FIELDS.has(a.actionField as string)
    );
    if (
      loopUnsafe.length > 0 &&
      !isTriggerSetSafe(
        "AND",
        trigger.map((t) => ({
          triggerFieldChange: t.triggerFieldChange,
          triggerEventType: t.triggerEventType ?? "onUpdate",
        }))
      )
    ) {
      setValidationError(
        'A "Create a Task", "Show a toast notification", or "Reset the timer" action requires a trigger that fires once — a change-based trigger ("changed to" / "changed from"), a "when a card is created" trigger, or a promise-time trigger.'
      );
      return;
    }
    if (
      hasCreateTask &&
      action.some(
        (a) =>
          a.actionField === "createTask" &&
          !parseCreateTaskActionValue(a.actionValue).title.trim()
      )
    ) {
      setValidationError('A "Create a Task" action requires a title.');
      return;
    }
    if (
      action.some(
        (a) =>
          a.actionField === "showToast" &&
          !parseShowToastActionValue(a.actionValue).headline.trim()
      )
    ) {
      setValidationError(
        'A "Show a toast notification" action requires a headline.'
      );
      return;
    }
    // Defensive backstop, not the primary defense — getSelectableConditionalTriggers
    // (used by conditionButtons above) already keeps the builder's own
    // picker from constructing this shape. Mirrors hasOrphanScheduleConditions
    // immediately below: cheap insurance against a future change to how the
    // trigger array is built reintroducing a contradictory pair (e.g. "is a
    // waiter" AND "is a drop-off"), which would otherwise save successfully
    // and silently never fire (WEBV3-216).
    if (hasContradictoryFixedValueConditions(trigger)) {
      setValidationError(
        "These conditions can never both be true at once — remove one before saving."
      );
      return;
    }
    // A trigger set that is "scheduled" but carries no schedule-DEFINING
    // trigger saves happily and can never fire on any path: evaluateTriggers
    // refuses scheduled automations, and the scanner's getSchedule() returns
    // null. Not reachable through this dialog anymore — the schedule trigger
    // always stays at index 0 now, so handleRemoveTrigger's index-0 branch
    // wipes it and any conditions together. Not reachable through import
    // either (automationImport.ts's own parenthood check is scoped
    // differently and never calls this). Kept as a defensive guard against a
    // future change to how the trigger array is built reintroducing this
    // shape.
    if (hasOrphanScheduleConditions(trigger)) {
      setValidationError(
        'These conditions need an "On a schedule" trigger — without one the automation can never run. Add the schedule back, or remove the conditions.'
      );
      return;
    }
    const incompleteSchedule = trigger.find(
      (t) =>
        t.triggerValueEditor === "schedule" &&
        !isScheduleValueComplete(parseScheduleTriggerValue(t.triggerValue))
    );
    if (incompleteSchedule) {
      setValidationError(
        'An "On a schedule" trigger set to specific days needs at least one day selected, and a valid time.'
      );
      return;
    }
    const automation: CreateAutomationDto = {
      triggersConditional: "AND",
      triggers: trigger.map((trig) => ({
        templateId: trig.id,
        triggerObject: trig.triggerObject,
        triggerEventType: trig.triggerEventType ?? "onUpdate",
        triggerField: trig.triggerField,
        triggerFieldChange: trig.triggerFieldChange,
        triggerCondition: trig.triggerCondition,
        triggerValue: trig.triggerValue,
      })),
      actions: action.map((a) => ({
        templateId: a.id,
        actionObject: a.actionObject,
        actionEventType: a.actionEventType,
        actionField: a.actionField,
        actionValue: a.actionValue,
        actionObjectIdentifier: "self",
      })),
    };
    onSubmit?.(automation);
    setOpen(false);
    resetDialog();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      resetDialog();
    }
    setOpen(open);
  };

  return (
    <Dialog onOpenChange={handleOpenChange} open={open}>
      <DialogTrigger asChild>
        <Button variant={"accent"} onClick={() => setOpen(true)}>
          Create Automation
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{"Create Automation"}</DialogTitle>
        </DialogHeader>
        <DialogDescription></DialogDescription>
        {showTriggerBuilderDialog && (
          <BuilderDialog
            buttonTitle={"When this happens"}
            title={"Trigger"}
            builderListProps={triggerButtons}
            onSubmit={async () => handleTriggerSelected()}
          />
        )}
        {trigger &&
          trigger.map((t, i) =>
            t.triggerValueEditor === "schedule" ? (
              <ScheduleValue
                key={t.id}
                label={t.buttonOptions.selectedText ?? t.buttonOptions.text}
                value={t.triggerValue}
                onChange={(raw) => handleTriggerValueChange(i, raw)}
                canAdd={conditionButtons && conditionButtons[i] !== undefined}
                addComponent={
                  conditionButtons &&
                  conditionButtons[i] !== undefined && (
                    <BuilderDialog
                      buttonTitle={<PlusIcon />}
                      buttonSize={"icon"}
                      disabled={!trigger}
                      title={"Select a condition"}
                      builderListProps={conditionButtons[i]}
                      onSubmit={async () => {}}
                    />
                  )
                }
                onRemove={() => handleRemoveTrigger(i)}
              />
            ) : (
              <Trigger
                key={t.id}
                label={t.buttonOptions.selectedText ?? t.buttonOptions.text}
                conditionalOptions={t.triggerConditionOptions}
                valuePlaceholder={
                  t.triggerField ? t.triggerPlaceholder : undefined
                }
                onValueChange={
                  t.triggerField && t.triggerValueEditable
                    ? (value) => handleTriggerValueChange(i, value)
                    : undefined
                }
                valueOptions={generateOptions(
                  t.triggerValueOptions,
                  t.triggerValueOptionsDynamic
                )}
                onConditionalChange={
                  t.triggerConditionOptions
                    ? (value) => handleTriggerConditionalChange(i, value)
                    : undefined
                }
                onAdd={undefined}
                canAdd={conditionButtons && conditionButtons[i] !== undefined}
                addComponent={
                  conditionButtons &&
                  conditionButtons[i] !== undefined && (
                    <BuilderDialog
                      buttonTitle={<PlusIcon />}
                      buttonSize={"icon"}
                      disabled={!trigger}
                      title={"Select a condition"}
                      builderListProps={conditionButtons[i]}
                      onSubmit={async () => {}}
                    />
                  )
                }
                onRemove={() => handleRemoveTrigger(i)}
              />
            )
          )}
        {trigger && isUnconditionedSchedule(trigger) && <ScheduleScopeAlert />}
        {showActionBuilderDialog && (
          <BuilderDialog
            buttonTitle={"Then do this"}
            disabled={!trigger}
            title={"Select an Action"}
            builderListProps={actionButtons}
            onSubmit={async () => handleActionSelected()}
          />
        )}
        {action &&
          action.map((a, i) =>
            a.actionField === "createTask" ? (
              <CreateTaskActionValue
                key={a.id}
                label={a.buttonOptions.selectedText ?? a.buttonOptions.text}
                value={a.actionValue}
                assigneeOptions={generateOptions(undefined, "assignees") ?? []}
                onChange={(raw) => handleActionValueChange(i, raw)}
                onRemove={() => handleRemoveAction(i)}
                canRemove={!a.required}
              />
            ) : a.actionField === "showToast" ? (
              <ShowToastActionValue
                key={a.id}
                label={a.buttonOptions.selectedText ?? a.buttonOptions.text}
                value={a.actionValue}
                onChange={(raw) => handleActionValueChange(i, raw)}
                onRemove={() => handleRemoveAction(i)}
                canRemove={!a.required}
              />
            ) : (
              <Action
                key={a.id}
                label={a.buttonOptions.selectedText ?? a.buttonOptions.text}
                valuePlaceholder={
                  a.actionField ? a.actionPlaceholder : undefined
                }
                valueOptions={generateOptions(
                  a.actionValueOptions,
                  a.actionValueOptionsDynamic
                )}
                multiSelect={a.multiSelect}
                onValueChange={
                  a.actionField && a.actionValueEditable
                    ? (value) => handleActionValueChange(i, value)
                    : undefined
                }
                onAdd={undefined}
                canAdd={additionalActions && additionalActions[i] !== undefined}
                addComponent={
                  additionalActions &&
                  additionalActions[i] !== undefined && (
                    <BuilderDialog
                      buttonTitle={<PlusIcon />}
                      buttonSize={"icon"}
                      disabled={!trigger}
                      title={"Select additional actions"}
                      builderListProps={additionalActions[i]}
                      onSubmit={async () => {}}
                    />
                  )
                }
                canRemove={!a.required}
                onRemove={() => handleRemoveAction(i)}
              />
            )
          )}
        {validationError && (
          // text-destructive, NOT text-red-500: tailwind.config.js sets `red`
          // to a flat hex string, which replaces Tailwind's default red scale,
          // so text-red-<N> is never generated and this rendered in the
          // ambient foreground colour instead of as an error.
          <p className={"text-sm text-destructive"}>{validationError}</p>
        )}
        <DialogFooter>
          <Button variant={"secondary"} onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button type={"submit"} onClick={() => handleSave()}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
